import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { GCPLog } from '@engine/models/admin'
import { DateTime, TrackingEvent } from '@engine/models/tracking'

import { ActivityTimeline } from '#components/ActivityTimeline'
import { AdminListLayout } from '#components/AdminListLayout'
import { Slider } from '#components/Slider.js'
import { Table } from '#components/Table'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import {
  UnmappedTrackEventsFilters,
  useUnmappedTrackEventsFilters,
} from './UnmappedTrackEventsFilters'

// Define the structure of the log data we expect
interface UnmappedTrackingLogData {
  scac: string
  targetId: string
  traceId: string
  events: TrackingEvent[]
}

// Extend GCPLog to include our specific data structure
interface UnmappedTrackingLog extends Omit<GCPLog, 'data'> {
  data: UnmappedTrackingLogData
}

const columnHelper = createColumnHelper<UnmappedTrackingLog>()

const formatDateTime = (dt?: DateTime) => {
  if (!dt) return ''
  const dateStr = dt.date
  const timeStr = dt.time ? ` ${dt.time}` : ''
  return `${dateStr}${timeStr}`
}

const EventCard = ({ event }: { event: TrackingEvent }) => {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-base-200 bg-base-100 p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-base-content/70">
          {formatDateTime(event.dateTime)}
        </span>
        {event.eventType && (
          <span className="badge badge-secondary badge-sm uppercase">
            {event.eventType}
          </span>
        )}
      </div>

      <div>
        <div className="font-semibold text-base-content">
          {event.eventDescription || 'No description'}
        </div>
        {event.carrierEventDescription &&
          event.carrierEventDescription !== event.eventDescription && (
            <div className="mt-1 text-xs text-base-content/60">
              Carrier: {event.carrierEventDescription}
            </div>
          )}
      </div>

      {(event.city || event.state || event.exceptionDescription) && (
        <div className="mt-2 flex flex-wrap gap-2 border-t border-base-200 pt-2 text-xs text-base-content/70">
          {(event.city || event.state) && (
            <span className="flex items-center gap-1">
              📍 {[event.city, event.state].filter(Boolean).join(', ')}
            </span>
          )}
          {event.exceptionDescription && (
            <span className="flex items-center gap-1 text-error">
              ⚠ {event.exceptionDescription}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const UnmappedTrackEventsReport = () => {
  const [selectedLog, setSelectedLog] = useState<UnmappedTrackingLog | null>(
    null,
  )
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(
    null,
  )

  const { filters, localFilters, setLocalFilters } =
    useUnmappedTrackEventsFilters()

  const {
    items: logs,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<UnmappedTrackingLog>(
    'unmapped-tracking-events',
    {
      limit: 50,
      ...filters,
    },
  )

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row.data?.targetId, {
        header: 'Load Number',
        cell: info => (
          <span
            className="font-medium cursor-pointer text-blue-500 hover:underline"
            onClick={() => setSelectedExternalId(info.getValue())}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: info => (
          <span className="text-xs">
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor(row => row.data?.scac, {
        header: 'Carrier',
        cell: info => (
          <span className="badge badge-sm badge-info uppercase">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.display({
        header: 'Events',
        cell: info => {
          const events = info.row.original.data?.events || []

          if (events.length === 0) {
            return null
          }

          return (
            <>
              <span className="text-xs">{events[0]?.eventDescription}</span>
              {events.length > 1 && (
                <span
                  className="ml-1 cursor-pointer text-xs text-blue-500 hover:underline"
                  onClick={() => setSelectedLog(info.row.original)}
                >
                  {`+ ${events.length - 1} more`}
                </span>
              )}
            </>
          )
        },
      }),
      columnHelper.accessor('trace', {
        header: 'Trace ID',
        cell: info => (
          <span className="font-mono text-xs text-base-content/50">
            {info.getValue()?.split('/').pop()}
          </span>
        ),
      }),
    ],
    [],
  )

  return (
    <AdminListLayout
      title="Unmapped Track Events"
      isLoading={isLoading}
      error={error}
      onRefresh={refresh}
      filters={
        <UnmappedTrackEventsFilters
          isLoading={isLoading}
          onRefresh={refresh}
          localFilters={localFilters}
          setLocalFilters={setLocalFilters}
        />
      }
      emptyMessage="No unmapped tracking events found in logs"
      listContent={
        logs.length > 0 ? (
          <div className="h-full">
            <Table
              data={logs}
              columns={columns}
              getRowId={log => log.id || log.timestamp}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
            <Slider
              isOpen={!!selectedLog}
              onClose={() => setSelectedLog(null)}
              title="Unmapped Events"
              width="50vw"
            >
              <div className="flex flex-col gap-3 p-4">
                {selectedLog?.data?.events?.map((event, index) => (
                  <EventCard key={index} event={event} />
                ))}
              </div>
            </Slider>

            <Slider
              isOpen={!!selectedExternalId}
              onClose={() => setSelectedExternalId(null)}
              title={`Activity Timeline: ${selectedExternalId}`}
              width="60vw"
            >
              <div className="p-4">
                {selectedExternalId && (
                  <ActivityTimeline externalId={selectedExternalId} />
                )}
              </div>
            </Slider>
          </div>
        ) : null
      }
    />
  )
}

export { UnmappedTrackEventsReport }
